import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { StravaActivity } from '../../services/StravaService';
import { Plan } from '../../services/PlansService';
import { AppContext } from '../../contexts/AppContext';
import { convertDistance, convertElevation, round } from '../../utils/UnitsUtils';

interface CompareStep3ReviewProps {
    activity?: StravaActivity;
    plan?: Plan;
}

const CompareStep3Review: React.FC<CompareStep3ReviewProps> = ({
    activity,
    plan,
}) => {
    const intl = useIntl();
    const { userService } = React.useContext(AppContext);

    const distanceUnits = userService.user?.distanceUnits || "km";
    const elevationUnits = userService.user?.elevationUnits || "m";

    if (!activity || !plan) {
        return (
            <Box sx={{ my: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    <FormattedMessage id="comparison.review.missingData" />
                </Typography>
            </Box>
        );
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
    };

    const planDistanceMeters = (plan.distance || 0) * 1000;
    const activityDistanceMeters = activity.distance;

    const planDistanceConverted = convertDistance(planDistanceMeters / 1000, distanceUnits);
    const activityDistanceConverted = convertDistance(activityDistanceMeters / 1000, distanceUnits);

    const distanceDiff = round(activityDistanceConverted - planDistanceConverted, 1);
    const distancePercent = planDistanceConverted > 0 ? Math.round((distanceDiff / planDistanceConverted) * 100) : 0;

    const planElevationMeters = plan.elevationGain || 0;
    const activityElevationMeters = activity.total_elevation_gain;

    const planElevationConverted = convertElevation(planElevationMeters, elevationUnits);
    const activityElevationConverted = convertElevation(activityElevationMeters, elevationUnits);

    const elevationDiff = round(activityElevationConverted - planElevationConverted, 0);
    const elevationPercent = planElevationConverted > 0 ? Math.round((elevationDiff / planElevationConverted) * 100) : 0;

    const plannedTime = plan.plannedEffort?.reduce((acc, effort) => acc + (effort?.time || 0), 0) || 0;

    const hasSignificantDifference = Math.abs(distancePercent) > 10 || Math.abs(elevationPercent) > 10;

    return (
        <Box sx={{ width: '100%', mt: 1 }}>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                <FormattedMessage id="comparison.review.description" />
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                <FormattedMessage id="comparison.review.plan" />
                            </Typography>

                            <Typography variant="subtitle1">{plan.name}</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {plan.date?.toLocaleDateString()}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="comparison.review.distance" />
                                    </Typography>
                                    <Typography variant="body1">
                                        {intl.formatMessage(
                                            { id: "data.valueUnit" },
                                            {
                                                value: planDistanceConverted,
                                                unit: intl.formatMessage({ id: distanceUnits })
                                            }
                                        )}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="comparison.review.elevation" />
                                    </Typography>
                                    <Typography variant="body1">
                                        {intl.formatMessage(
                                            { id: "data.valueUnit" },
                                            {
                                                value: planElevationConverted,
                                                unit: intl.formatMessage({ id: elevationUnits })
                                            }
                                        )}
                                    </Typography>
                                </Grid>

                                {!!plannedTime && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            <FormattedMessage id="comparison.review.plannedTime" />
                                        </Typography>
                                        <Typography variant="body1">
                                            {formatDuration(plannedTime * 60)}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                <FormattedMessage id="comparison.review.activity" />
                            </Typography>

                            <Typography variant="subtitle1">{activity.name}</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {formatDate(activity.start_date)} • {activity.type}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="comparison.review.distance" />
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: "center",
                                        color: Math.abs(distancePercent) > 10 ? 'error.main' : 'text.primary'
                                    }}>
                                        {intl.formatMessage(
                                            { id: "data.valueUnit" },
                                            {
                                                value: activityDistanceConverted,
                                                unit: intl.formatMessage({ id: distanceUnits })
                                            }
                                        )}
                                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                            ({distanceDiff > 0 ? '+' : ''}{intl.formatMessage(
                                                { id: "data.valueUnit" },
                                                {
                                                    value: distanceDiff,
                                                    unit: intl.formatMessage({ id: distanceUnits })
                                                }
                                            )}, {distancePercent}%)
                                        </Typography>
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="comparison.review.elevation" />
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: "center",
                                        color: Math.abs(elevationPercent) > 10 ? 'error.main' : 'text.primary'
                                    }}>
                                        {intl.formatMessage(
                                            { id: "data.valueUnit" },
                                            {
                                                value: activityElevationConverted,
                                                unit: intl.formatMessage({ id: elevationUnits })
                                            }
                                        )}
                                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                            ({elevationDiff > 0 ? '+' : ''}{intl.formatMessage(
                                                { id: "data.valueUnit" },
                                                {
                                                    value: elevationDiff,
                                                    unit: intl.formatMessage({ id: elevationUnits })
                                                }
                                            )}, {elevationPercent}%)
                                        </Typography>
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="comparison.review.movingTime" />
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDuration(activity.moving_time)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="comparison.review.elapsedTime" />
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDuration(activity.elapsed_time)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {hasSignificantDifference ? (
                <Box sx={{ mt: 4, p: 3, bgcolor: 'warning.light', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon color="warning" sx={{ mr: 2 }} />
                    <Box>
                        <Typography fontWeight="bold" gutterBottom>
                            <FormattedMessage id="comparison.review.caution.title" />
                        </Typography>
                        <Typography>
                            <FormattedMessage 
                                id="comparison.review.caution.message" 
                                values={{
                                    distanceDiff: Math.abs(distancePercent),
                                    elevationDiff: Math.abs(elevationPercent)
                                }}
                            />
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ mt: 4, p: 3, bgcolor: 'success.light', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                    <Typography>
                        <FormattedMessage id="comparison.review.readyToCompare" />
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default CompareStep3Review;